import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import CategorySection from './components/CategorySection';
import ValueSection from './components/ValueSection';
import SpreadsheetSection from './components/SpreadsheetSection';
import SearchSection from './components/SearchSection';
import ComparisonSection from './components/ComparisonSection';
import HowSection from './components/HowSection';
import PillarSection from './components/PillarSection';
import TrustSection from './components/TrustSection';
import FaqSection from './components/FaqSection';
import CtaSection from './components/CtaSection';
import { mainSiteUrl } from '@/mocks/categories';
import { SITE_URL, SITE_FAVICON } from '@/lib/site';

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'USFansLink',
  url: `${SITE_URL}/`,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'USFansLink',
  url: `${SITE_URL}/`,
  logo: SITE_FAVICON,
};

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'USFans Spreadsheet — USFans Creators, Guides & Resources',
  description:
    'Explore the USFans Spreadsheet — a structured resource hub for USFans creators, guides, shipping costs, coupons, warehouse info and safety tips.',
  url: `${SITE_URL}/`,
  inLanguage: 'en',
};

export default function Home() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-cream">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }} />
      <Navbar transparent />
      <main>
        <Hero />

        {/* Hero support band */}
        <section className="bg-white">
          <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-12 text-center md:px-6">
            <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
              {t('heroSupport.title')}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-stone-500 md:text-base">
              {t('heroSupport.body')}
            </p>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-stone-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-800"
            >
              {t('heroSupport.cta')}
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </section>

        <CategorySection />
        <ValueSection />
        <SpreadsheetSection />
        <SearchSection />
        <PillarSection />
        <ComparisonSection />
        <HowSection />

        {/* Article teaser */}
        <section className="bg-white">
          <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6">
            <div className="flex flex-col items-start justify-between gap-6 rounded-lg border border-emerald-200 bg-emerald-50 p-8 md:flex-row md:items-center md:p-10">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">
                  <i className="ri-article-line"></i>
                  USFans Spreadsheet
                </span>
                <h2 className="mt-4 font-heading text-xl font-bold text-stone-900 md:text-2xl">
                  {t('spreadsheet.title')}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">
                  {t('heroSupport.body')}
                </p>
              </div>
              <Link
                to="/article"
                className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-emerald-600 px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                {t('cta.readArticle')}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <TrustSection />
        <FaqSection />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}